<script setup lang="ts">
//#region imports
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import { useApi } from '@/api';
import { useTypedStore } from '@/store';
import { isErrorResponse } from '@/types/core';
import UploadFile from '@/components/UploadFile.vue';
import type { EmitsToListeners, FormErrors, UploadedFile } from '@/types/core';
import type {
  PostAffiliatesByIdCreatePaymentResponse,
  PostPaymentsHistoryExternalData,
} from '@/types/openapi-ts';
//#endregion

//#region types
interface FormType {
  type: 'internal' | 'external';
  amount: number | string;
  currency_code: string;
  accounting_date: Date | null;
  comment: string;
}

interface Props {
  affiliateId: string;
  affiliateEmail?: string;
  canCreateInternal?: boolean;
  canCreateExternal?: boolean;
}

interface Emits {
  success: [payload?: PostAffiliatesByIdCreatePaymentResponse['payload']];
  close: [];
}

export interface CreatePaymentParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  affiliateEmail: '',
  canCreateInternal: false,
  canCreateExternal: false,
});
const emit = defineEmits<Emits>();
//#endregion

//#region composables
const api = useApi();
const store = useTypedStore();
const { t } = useI18n();
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);
const isFilesLoading = ref(false);
const errors = ref<FormErrors>({});
const files = ref<UploadedFile[]>([]);
const form = ref<FormType>(getDefaultForm());
//#endregion

//#region computed
const currencies = computed(() => store.getters['misc/currencies']);
const isExternal = computed(() => form.value.type === 'external');
const typeOptions = computed(() => ([
  {
    label: t('payments.types.internal'),
    value: 'internal' as FormType['type'],
    disabled: !props.canCreateInternal,
  },
  {
    label: t('payments.types.external'),
    value: 'external' as FormType['type'],
    disabled: !props.canCreateExternal,
  },
]));
const confirmMessage = computed(() => (
  t(`affiliates.confirm.createPayment.msg_${form.value.type}`, {
    affiliateEmail: props.affiliateEmail,
  })
));
const isPrimaryDisabled = computed(() => (
  isLoading.value
  || isFilesLoading.value
  || (form.value.type === 'internal' && !props.canCreateInternal)
  || (form.value.type === 'external' && !props.canCreateExternal)
));
//#endregion

//#region methods
function getDefaultForm(): FormType {
  return {
    type: props.canCreateInternal ? 'internal' : 'external',
    amount: '',
    currency_code: store.getters['misc/activeCustomerCurrency']?.code || '',
    accounting_date: new Date(),
    comment: '',
  };
}

function open() {
  isVisible.value = true;
}

function handleClose() {
  emit('close');
  isVisible.value = false;
}

function getExternalPayload(): PostPaymentsHistoryExternalData['body'] {
  const fileIds = files.value
    .map((file) => file.uploadId)
    .filter((id): id is string => !!id);

  return {
    affiliate_id: props.affiliateId,
    amount: Number(form.value.amount),
    currency_code: form.value.currency_code,
    accounting_date: form.value.accounting_date
      ? format(form.value.accounting_date, 'yyyy-MM-dd')
      : '',
    comment: form.value.comment,
    file_ids: fileIds,
  };
}

function submitInternal() {
  isLoading.value = true;
  errors.value = {};

  api.createPaymentAffiliate(props.affiliateId)
    .then((response) => {
      emit('success', response.data.payload);
      handleClose();
    })
    .catch((error: unknown) => {
      if (isErrorResponse(error)) {
        errors.value = error.data.errors;
      }
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function submitExternal() {
  isLoading.value = true;
  errors.value = {};

  api.postPaymentsHistoryExternal(getExternalPayload())
    .then(() => {
      emit('success');
      handleClose();
    })
    .catch((error: unknown) => {
      if (isErrorResponse(error)) {
        errors.value = error.data.errors;
      }
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function handlePrimaryClick() {
  if (form.value.type === 'internal') {
    submitInternal();
    return;
  }

  submitExternal();
}

function handleFilesLoading(value: boolean) {
  isFilesLoading.value = value;
}
//#endregion

defineExpose({
  open,
  handleClose,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="create-payment-popup"
    :title="$t('affiliates.confirm.createPayment.title')"
    :is-loading="isLoading"
    :width="480"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <CardTitle :title="$t('affiliates.confirm.createPayment.type')">
          <UiRadioGroup
            v-model="form.type"
            :options="typeOptions"
          />
        </CardTitle>
        <div
          v-if="!isExternal"
          class="t:flex t:items-center t:gap-12"
        >
          <UiIcon
            :size="24"
            name="money-bill"
            color="var(--text-positive)"
          />
          <div
            class="create-payment-popup__confirm-msg"
            v-html="confirmMessage"
          />
        </div>
        <template v-else>
          <div class="create-payment-popup__hint">
            <UiIcon
              :size="18"
              name="exclamation-triangle"
              color="var(--text-warning)"
            />
            <div>{{ $t('affiliates.confirm.createPayment.hint') }}</div>
          </div>
          <div class="t:grid t:grid-cols-2 t:gap-16">
            <UiInput
              v-model="form.amount"
              :label="$t('payments.info.amount')"
              placeholder="0"
              size="big"
              type="number"
              autosize
              :error="errors.amount"
            />
            <UiSelectElement
              v-model="form.currency_code"
              :label="$t('affiliates.affiliatesInfo.currency.label')"
              :list="currencies"
              size="big"
              filterable
              option-value-param="code"
              option-label-param="name"
              search-param="code"
              :error="errors.currency_code"
            />
          </div>
          <UiDatePicker
            v-model="form.accounting_date"
            :label="$t('affiliates.affiliatesActivity.date')"
            :placeholder="$t('affiliates.confirm.createPayment.datePlaceholder')"
            size="big"
            :range="false"
            :multi-calendars="false"
            :clearable="false"
            teleport
            external-format="dd MMM yyyy"
            :error="errors.accounting_date"
          />
          <UiTextarea
            v-model="form.comment"
            :label="$t('affiliates.confirm.createPayment.note')"
            :placeholder="$t('affiliates.confirm.createPayment.notePlaceholder')"
            :rows="5"
            :error="errors.comment"
          />
          <UploadFile
            v-model="files"
            :title="$t('payments.uploadFilesPopup.label')"
            is-dragger
            multiple
            :error="errors.file_ids"
            @is-loading="handleFilesLoading"
          />
        </template>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          :disabled="isLoading"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="money-bill"
          :disabled="isPrimaryDisabled"
          @click="handlePrimaryClick"
        >
          {{ $t('affiliates.confirm.createPayment.action') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.create-payment-popup {
  &__hint {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
    border-radius: 8px;
    background: var(--background-warning-subtle);
    color: var(--text-body);
    font-size: 14px;
    line-height: 24px;
  }
  &__confirm-msg {
    color: var(--text-body);
    font-size: 14px;
    line-height: 24px;

    > span {
      font-weight: 500;
    }
  }
}
</style>
